import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { IMAGES } from "../constants/theme";
import apiClient from "../api/apiClient";
import { useLanguage } from "../i18n/LanguageContext";

// Long enough to read the headline and the line under it before it moves on.
const INTERVAL = 11000;
const TEASER_VIDEO = `${process.env.PUBLIC_URL}/videos/home2-teaser-hero.mp4`;

const FALLBACK_SLIDES = [
  {
    img: IMAGES.heroPhoto1,
    label: "STRENGTH",
    headline: ["BUILD", "STRENGTH"],
    message: "Every rep, every set builds progress in the details. Show up and do the work.",
  },
  {
    img: IMAGES.heroPhoto2,
    label: "POWER",
    headline: ["UNLEASH", "YOUR POWER"],
    message: "Explosive, intentional, unstoppable. Power is never given; it is earned through consistency.",
  },
  {
    img: IMAGES.heroPhoto3,
    label: "PERFORMANCE",
    headline: ["ELEVATE", "PERFORMANCE"],
    message: "Train with purpose. Every session is an opportunity to become a better version of yourself.",
  },
  {
    img: IMAGES.heroPhoto4,
    label: "DISCIPLINE",
    headline: ["STAY", "DISCIPLINED"],
    message: "Champions aren't made on their best days. They're built in the quiet moments of commitment.",
  },
];

const Home2Banner = ({ open }) => {
  const { t } = useLanguage();
  const [slides, setSlides] = useState(FALLBACK_SLIDES);
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [isMobileHero, setIsMobileHero] = useState(false);
  const videoRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    apiClient
      .get("/homepage/hero-slides")
      .then(({ data }) => {
        if (data && data.length > 0) {
          setSlides(
            data.map((s) => ({
              img: s.image_url,
              label: s.label,
              headline: [s.headline_1, s.headline_2],
              message: s.message,
            }))
          );
        }
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    const mobileQuery = window.matchMedia("(max-width: 767px)");
    const updateMobileHero = () => setIsMobileHero(mobileQuery.matches);

    updateMobileHero();
    mobileQuery.addEventListener?.("change", updateMobileHero);
    mobileQuery.addListener?.(updateMobileHero);

    return () => {
      mobileQuery.removeEventListener?.("change", updateMobileHero);
      mobileQuery.removeListener?.(updateMobileHero);
    };
  }, []);

  const playHeroVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;

    const playPromise = video.play?.();
    if (playPromise?.catch) playPromise.catch(() => {});
  }, []);

  useEffect(() => {
    playHeroVideo();
  }, [isMobileHero, playHeroVideo]);

  useEffect(() => {
    const retryPlayback = () => playHeroVideo();

    window.addEventListener("touchstart", retryPlayback, { once: true, passive: true });
    window.addEventListener("pageshow", retryPlayback);
    document.addEventListener("visibilitychange", retryPlayback);

    return () => {
      window.removeEventListener("touchstart", retryPlayback);
      window.removeEventListener("pageshow", retryPlayback);
      document.removeEventListener("visibilitychange", retryPlayback);
    };
  }, [playHeroVideo]);

  // Parallax: video drifts at 35% scroll speed, content at 12%
  useEffect(() => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) return;

    const onScroll = () => {
      if (window.innerWidth < 768) return;
      const y = window.scrollY;
      if (videoRef.current) videoRef.current.style.transform = `translateY(${y * 0.35}px)`;
      if (contentRef.current) contentRef.current.style.transform = `translateY(${y * 0.12}px)`;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = useCallback(
    (index) => {
      if (animating || index === current) return;
      setAnimating(true);
      setCurrent(index);
      setTimeout(() => setAnimating(false), 700);
    },
    [animating, current]
  );

  const slideCount = slides.length;
  const activeIndex = current;
  const activeSlide = slides[activeIndex] || FALLBACK_SLIDES[0];

  // Wraps in both directions so neither arrow is ever a dead end.
  const step = useCallback(
    (offset) => goTo((current + offset + slideCount) % slideCount),
    [goTo, current, slideCount],
  );

  useEffect(() => {
    const t = setInterval(() => {
      setCurrent((c) => (c + 1) % slideCount);
    }, INTERVAL);
    return () => clearInterval(t);
  }, [slideCount]);

  return (
    <div className="hero-banner">
      {/* ── Single full-bleed video background ── */}
      <div className="hero-video-bg">
        <video
          ref={videoRef}
          className="hero-bg-video"
          autoPlay
          muted
          defaultMuted
          loop
          playsInline
          webkit-playsinline="true"
          preload="auto"
          poster={activeSlide.img}
          onCanPlay={playHeroVideo}
          onLoadedData={playHeroVideo}
          onLoadedMetadata={playHeroVideo}
          aria-hidden="true"
        >
          <source src={TEASER_VIDEO} type="video/mp4" />
        </video>
        {/* Minimal vignette — clears at 40% so the right side of the video is fully visible */}
        <div className="hero-vignette" />
      </div>

      {/* ── Text content ── */}
      <div className="hero-content-wrap" ref={contentRef}>
        <div className="container">
          <div className="hero-content-inner">
            <div key={`label-${activeIndex}`} className="hero-label">
              {t(activeSlide.label)}
            </div>

            <h1 key={`headline-${activeIndex}`} className="hero-headline">
              <span>{t(activeSlide.headline[0])}</span>
              <span className="hero-headline-accent">{t(activeSlide.headline[1])}</span>
            </h1>

            <p key={`msg-${activeIndex}`} className="hero-message">
              {t(activeSlide.message)}
            </p>

            <div className="hero-ctas">
              <Link to="/libraries" className="btn btn-primary btn-skew btn-lg">
                <span>{t("Explore Libraries")}</span>
              </Link>
            </div>

            <div className="hero-dots">
              {slides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => goTo(i)}
                  className={`hero-dot${i === current ? " active" : ""}`}
                  aria-label={t("Show slide {number}", { number: i + 1 })}
                />
              ))}
              <span className="hero-counter">
                0{activeIndex + 1} / 0{slideCount}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Previous / next ── */}
      <button
        type="button"
        className="hero-arrow hero-arrow-prev"
        onClick={() => step(-1)}
        aria-label={t("Previous slide")}
      >
        <i className="fa-solid fa-chevron-left" aria-hidden="true" />
      </button>
      <button
        type="button"
        className="hero-arrow hero-arrow-next"
        onClick={() => step(1)}
        aria-label={t("Next slide")}
      >
        <i className="fa-solid fa-chevron-right" aria-hidden="true" />
      </button>

      {/* ── Vertical slide indicators ── */}
      <div className="hero-bars">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => goTo(i)}
            className={`hero-bar${i === current ? " active" : ""}`}
            aria-label={`Show slide ${i + 1}`}
          />
        ))}
      </div>

      <style>{`
        /* ── Shell ── */
        .hero-banner {
          position: relative;
          height: 100vh;
          min-height: 560px;
          background: #0d0d0d;
          overflow: hidden;
        }

        /* ── Video container — oversized vertically so parallax never shows a gap ── */
        .hero-video-bg {
          position: absolute;
          top: -20%;
          bottom: -20%;
          left: 0;
          right: 0;
          z-index: 1;
        }

        .hero-bg-video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center 20%;
          display: block;
          will-change: transform;
        }

        /*
         * Vignette strategy: left-edge gradient keeps text readable;
         * bottom fade grounds the layout. Right 50%+ is untouched.
         */
        .hero-vignette {
          position: absolute;
          inset: 0;
          background:
            linear-gradient(90deg,
              rgba(10,10,10,0.72) 0%,
              rgba(10,10,10,0.38) 32%,
              rgba(10,10,10,0.06) 54%,
              transparent 68%
            ),
            linear-gradient(180deg,
              transparent 45%,
              rgba(10,10,10,0.40) 100%
            );
          pointer-events: none;
        }

        /* ── Content ── */
        .hero-content-wrap {
          position: absolute;
          inset: 0;
          z-index: 4;
          display: flex;
          align-items: center;
          padding-top: 80px;
          will-change: transform;
        }

        /* Push card flush with the left viewport edge */
        .hero-content-wrap .container {
          padding-left: 0;
        }

        /*
         * Frosted glass card — only behind the text block.
         * The rest of the hero is pure video.
         */
        .hero-content-inner {
          max-width: 640px;
          padding: 36px 40px;
          background: rgba(8,8,8,0.36);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 2px;
        }

        .hero-label {
          display: inline-block;
          background: var(--primary, #c0392b);
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 4px;
          padding: 6px 18px;
          border-radius: 2px;
          margin-bottom: 18px;
          animation: hpaFadeUp 0.5s ease forwards;
        }

        .hero-headline {
          color: #fff;
          font-size: clamp(38px, 8vw, 84px);
          font-weight: 900;
          line-height: 1.0;
          margin-bottom: 18px;
          text-transform: uppercase;
          animation: hpaFadeUp 0.55s ease forwards;
        }
        .hero-headline span { display: block; }
        .hero-headline-accent { color: var(--primary, #c0392b); }

        .hero-message {
          color: rgba(255,255,255,0.80);
          font-size: 15px;
          line-height: 1.85;
          margin-bottom: 32px;
          animation: hpaFadeUp 0.65s ease 0.08s both;
        }

        .hero-ctas {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 28px;
        }
        .hero-btn-outline {
          border: 2px solid rgba(255,255,255,0.3) !important;
          color: #fff !important;
          background: transparent !important;
        }

        /* ── Dots ── */
        .hero-dots {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .hero-dot {
          width: 10px; height: 4px;
          border-radius: 2px; border: none;
          background: rgba(255,255,255,0.25);
          cursor: pointer;
          transition: all 0.4s ease;
          padding: 0;
        }
        .hero-dot.active {
          width: 36px;
          background: var(--primary, #c0392b);
        }
        .hero-counter {
          color: rgba(255,255,255,0.35);
          font-size: 12px;
          letter-spacing: 2px;
          margin-left: 6px;
        }

        /* ── Previous / next arrows ── */
        .hero-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 6;
          display: grid;
          place-items: center;
          width: 52px;
          height: 52px;
          padding: 0;
          border: 1px solid rgba(255,255,255,0.28);
          border-radius: 50%;
          background: rgba(8,8,8,0.42);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          color: #fff;
          font-size: 17px;
          cursor: pointer;
          transition: background-color 0.3s ease, border-color 0.3s ease;
        }
        .hero-arrow:hover {
          background: var(--primary, #c0392b);
          border-color: var(--primary, #c0392b);
        }
        .hero-arrow:focus-visible {
          outline: 3px solid #ff9a70;
          outline-offset: 3px;
        }
        .hero-arrow-prev { left: 24px; }
        .hero-arrow-next { right: 24px; }

        /* The vertical bars sit on the right, so the arrow steps aside. */
        @media (min-width: 768px) {
          .hero-arrow-next { right: 62px; }
        }

        @media (max-width: 767px) {
          .hero-arrow {
            width: 42px;
            height: 42px;
            font-size: 14px;
            top: 38%;
          }
          .hero-arrow-prev { left: 10px; }
          .hero-arrow-next { right: 10px; }
        }

        /* ── Vertical bars ── */
        .hero-bars {
          display: none;
          position: absolute;
          right: 20px; top: 50%;
          transform: translateY(-50%);
          z-index: 5;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }
        .hero-bar {
          width: 3px; height: 18px;
          border: none; border-radius: 2px;
          background: rgba(255,255,255,0.25);
          cursor: pointer; transition: all 0.4s ease; padding: 0;
        }
        .hero-bar.active {
          height: 44px;
          background: var(--primary, #c0392b);
        }

        /* ── Responsive ── */
        @media (min-width: 768px) {
          .hero-bars { display: flex; }
        }

        @media (min-width: 1400px) {
          .hero-content-inner { max-width: 720px; }
        }

        @media (min-width: 768px) and (max-width: 1100px) {
          .hero-content-inner { max-width: 580px; }
        }

        @media (max-width: 767px) {
          .hero-banner {
            height: max(520px, calc(100dvh - var(--hpa-header-stack-height, 80px)));
            min-height: 0;
            max-height: calc(100dvh - var(--hpa-header-stack-height, 80px));
          }
          /* No parallax offset on mobile - keeps the video locked in place */
          .hero-video-bg {
            top: 0;
            bottom: 0;
          }
          .hero-bg-video {
            height: 100%;
            transform: none !important;
            object-position: center center;
          }
          .hero-content-wrap {
            padding-top: 18px;
            padding-bottom: max(18px, env(safe-area-inset-bottom));
            align-items: flex-end;
            transform: none !important;
          }
          .hero-content-wrap .container {
            width: 100%;
            padding-right: 14px;
            padding-left: 14px;
          }
          .hero-content-inner {
            width: 100%;
            max-width: 100%;
            max-height: calc(100% - 24px);
            padding: 16px 14px;
            overflow: auto;
            -webkit-overflow-scrolling: touch;
          }
          .hero-label {
            margin-bottom: 10px;
            font-size: 10px;
            letter-spacing: 2px;
            padding: 5px 12px;
          }
          .hero-headline {
            margin-bottom: 10px;
            font-size: clamp(30px, 10.5vw, 48px);
            line-height: 0.96;
          }
          .hero-message {
            font-size: 13px;
            line-height: 1.55;
            margin-bottom: 16px;
          }
          .hero-ctas {
            display: grid;
            grid-template-columns: 1fr;
            gap: 8px;
            margin-bottom: 16px;
          }
          .hero-ctas .btn {
            width: 100%;
            min-height: 42px;
            padding: 9px 14px;
            font-size: 12px;
            line-height: 1.2;
          }
          .hero-dots {
            gap: 7px;
          }
          .hero-counter {
            font-size: 11px;
          }
          .hero-bars {
            display: none !important;
          }
          /* On mobile shift gradient to bottom so the full frame is visible */
          .hero-vignette {
            background:
              linear-gradient(180deg,
                rgba(10,10,10,0.08) 0%,
                rgba(10,10,10,0.18) 38%,
                rgba(10,10,10,0.76) 100%
              );
          }
        }

        @media (max-width: 400px) {
          .hero-banner {
            height: max(480px, calc(100dvh - var(--hpa-header-stack-height, 72px)));
            max-height: calc(100dvh - var(--hpa-header-stack-height, 72px));
          }
          .hero-headline { font-size: clamp(28px, 10vw, 42px); }
          .hero-message { font-size: 12px; }
        }

        @media (max-width: 767px) and (max-height: 640px) {
          .hero-banner {
            height: calc(100dvh - var(--hpa-header-stack-height, 72px));
            max-height: calc(100dvh - var(--hpa-header-stack-height, 72px));
          }
          .hero-content-inner {
            padding: 12px;
          }
          .hero-label,
          .hero-dots {
            display: none;
          }
          .hero-headline {
            font-size: clamp(26px, 9vw, 38px);
            margin-bottom: 8px;
          }
          .hero-message {
            margin-bottom: 12px;
          }
          .hero-ctas {
            margin-bottom: 0;
          }
        }

        @media (max-height: 720px) and (min-width: 768px) {
          .hero-content-inner { max-width: 560px; padding: 24px 32px; }
        }

        /* ── Animations ── */
        @keyframes hpaFadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-bg-video,
          .hero-content-wrap { will-change: auto; }
          .hero-label,
          .hero-headline,
          .hero-message { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </div>
  );
};

export default Home2Banner;
